import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';

import InputLabel from '@mui/material/InputLabel';
import { makeStyles } from '@mui/styles';

import { useT } from 'UI/lib';

// @ts-ignore
import 'quill/dist/quill.snow.css';
// @ts-ignore
import './styles.css';

const formats = [
  'header',
  'height',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'color',
  'bullet',
  'indent',
  'link',
  'image',
  'align',
  'size',
  'undo',
  'redo',
];
const icons = Quill.import('ui/icons');
icons['undo'] = `<svg viewbox='0 0 18 18'>
    <polygon class='ql-fill ql-stroke' points='6 10 4 12 2 10 6 10'></polygon>
    <path class='ql-stroke' d='M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9'></path>
  </svg>`;
icons['redo'] = `<svg viewbox='0 0 18 18'>
    <polygon class='ql-fill ql-stroke' points='12 10 14 12 16 10 12 10'></polygon>
    <path class='ql-stroke' d='M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5'></path>
  </svg>`;

const useStyles = makeStyles((theme: any) => ({
  wrapper: {
    color: (props: any) => (props.disabled ? '#101010' : theme.palette.custom.primary),
    width: '100%',
    '& *': {
      '& option': {
        'z-index': 2,
      },
      // apply font family to all text in RichTextEditor
      '& > *': {
        fontFamily: theme.typography.fontFamily,
      },
    },
    '& .ql-toolbar': {
      borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    },
    '& .ql-container': {
      borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
      '&.ql-disabled': {
        borderRadius: `${theme.shape.borderRadius}px`,
      },
      '& .ql-editor.ql-blank': {
        '&::before': {
          position: 'initial',
        },
        '& p': {
          display: 'none',
        },
      },
    },
  },
}));

interface NotesProps {
  value: string;
  onValueChange?: (value: string) => void;
  label?: string;
  disabled?: boolean;
  expandedEditor?: boolean;
  customToolbar?: [unknown] | null;
  toolbarAriaLabels?: string[];
  placeholder?: string;
}

const Notes = ({
  value,
  onValueChange = () => {},
  label = '',
  disabled = false,
  expandedEditor = false,
  customToolbar = null,
  toolbarAriaLabels = [],
  placeholder,
}: NotesProps) => {
  const classes = useStyles({ disabled });
  const t = useT();
  const quillRef = useRef(null);
  const ref = useRef(null);

  const [notes, setNotes] = useState(value || '');

  const onValueChangeCallback = useCallback(
    value => {
      setNotes(value);
      onValueChange(value.toString('html'));
    },
    [onValueChange]
  );

  useEffect(() => {
    const containerInstance = ref.current;
    if (containerInstance && !disabled) {
      const buttons = containerInstance.querySelector('.ql-toolbar').querySelectorAll('button');
      buttons.forEach((button, index) => {
        button.setAttribute('aria-label', `Button ${toolbarAriaLabels[index] || index}`);
      });
    }
    if (containerInstance && !disabled && containerInstance.querySelector('.ql-picker-label')) {
      containerInstance
        .querySelector('.ql-picker-label')
        .setAttribute('aria-label', 'Picker select');
    }
  }, [toolbarAriaLabels]);

  const myUndo = () => {
    if (!quillRef.current) {
      return;
    }
    let myEditor = quillRef.current.getEditor();
    return myEditor.history.undo();
  };

  const myRedo = () => {
    let myEditor = quillRef.current.getEditor();
    return myEditor.history.redo();
  };

  const modules = useMemo(
    () => ({
      toolbar: disabled
        ? false
        : {
            container: customToolbar ?? [
              ['bold', 'italic', 'underline'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ header: ['1', '2', '3', false] }],
              ['undo', 'redo'],
            ],
            handlers: {
              undo: () => myUndo(),
              redo: () => myRedo(),
            },
          },
      history: {
        delay: 2500,
        maxStack: 150,
        userOnly: true,
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    [customToolbar, disabled]
  );

  return (
    <div className={classes.wrapper} data-test="text-editor-part" ref={ref}>
      <InputLabel variant="standard">{label}</InputLabel>
      <ReactQuill
        tabIndex={0}
        ref={quillRef}
        value={notes}
        theme="snow"
        modules={modules}
        formats={formats}
        placeholder={placeholder ? `${placeholder}` : `${t('common:add_notes')}...`}
        onChange={onValueChangeCallback}
        readOnly={disabled}
        className={`text-editor ${expandedEditor ? 'expanded' : ''}`}
      />
    </div>
  );
};

export default Notes;
